import React from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';

import { useAppDispatch, useAppSelector } from '../../../store';
import { DataElementContext } from '../../../page-components/common/DataElementContext';
import {
  loginError,
  loginMethod,
  loginPassword,
  loginStart,
  loginUsername,
  requestSmsCode,
  resetRegisterPartialAccountData,
} from '@/modules/casino/store/actions';
import { IS_2FA, IS_EMAIL, IS_PHONE, IS_UNDEFINED, IS_USERNAME } from '@/modules/casino/utils/LoginMethods';
import { useTranslation } from 'react-i18next';

import './index.scss';
import { useFlutterEvents } from '@/components/utils/hooks/events/useFlutterEvents';
import { GLOBAL_EVENTS, GLOBAL_FLUTTER_CHANNELS } from '@/components/utils/hooks/events/constants';
import { clear2FADetails, send2FAcode } from '@/modules/casino/store/actions/authentication';

type LoginProps = {
  children: any;
  styleText: string;
  className: string;
  properties?: {
    dsType: string;
    preventRedirect: boolean;
    title: string;
    subTitle: string;
  };
};

const defaultProps = {
  className: '',
  styleText: '',
  properties: {
    dsType: '',
    preventRedirect: false,
    title: '',
    subTitle: '',
  },
};

const ModuleElementDiv = styled.div<{ $styleText: string }>((props) => props.$styleText);


const TWO_FA_METHHOD_ID = {
  EMAIL: 1,
  SMS: 2,
};

const Login = (componentProps: LoginProps) => {
  const tmpProps = { ...defaultProps, ...componentProps };
  delete tmpProps.children;
  const props = JSON.parse(JSON.stringify(tmpProps));
  const { children } = componentProps;
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const authentication = useAppSelector((state) => state.authentication);
  const login = useAppSelector((state) => state.login);
  const { existingAccount, existingAccountData } = useAppSelector<any>((state) => state.register.partialAccount);
  const events = useFlutterEvents();

  const {method2FADetails, code2FADetails} = authentication;
  const [currentStep, setCurrentStep] = React.useState('login');
  const [selected2FAMethod, setSelected2FAMethod] = React.useState(null);
  const [code2FAExpiresIn, setCode2FAExpiresIn] = React.useState<number | null>(null);

  const methods2FA = React.useMemo(() => {
    const methods: any[] = [];
    if (method2FADetails) {
      Object.keys(method2FADetails).forEach((key: any) => {
        const method:any = {
          value: method2FADetails[key]
        };
        method.id = key === 'email' ? TWO_FA_METHHOD_ID.EMAIL : (key === 'phone' ? TWO_FA_METHHOD_ID.SMS : 3);
        method.onSelect = () => select2FAMethodHandler(method.id);
        // @ts-ignore
        method.selected = selected2FAMethod ? (selected2FAMethod.id === method.id) : false;
        methods[method.id] = (method);
      });
    }
    return methods;
  }, [method2FADetails, selected2FAMethod]);

  const methods2FANo = React.useMemo(() => (Object.keys(methods2FA).length),[methods2FA]);

  const select2FAMethodHandler = React.useCallback((methodId: number) => {
    // find method in method2FADetails
    const method = { ...methods2FA[methodId] };
    method.selected = true;
    setSelected2FAMethod(method);
  }, [methods2FA]);

  const confirm2FASelectedMethod = React.useCallback(() => {
    if (selected2FAMethod) {
      // @ts-ignore
      dispatch(send2FAcode({ ...selected2FAMethod,username: username }));
      dispatch(loginMethod(IS_2FA)); // set login method!
      setCurrentStep('2fa');
    }
  }, [selected2FAMethod]);

  React.useEffect(() => {
    const keys = Object.keys(methods2FA);

    if (methods2FANo > 0) {
      setSelected2FAMethod(methods2FA[keys[0]]);
      if (methods2FANo > 1) {
        setCurrentStep('select-2fa-method');
      } else {
        setTimeout(() => {
          // @ts-ignore
          dispatch(loginMethod(IS_2FA));
          dispatch(send2FAcode({ ...methods2FA[keys[0]],username: username }));
          setCurrentStep('2fa');
        },50);
      }
    } else {
      setCurrentStep('login');
    }
  },[methods2FANo]);

  const { t } = useTranslation();

  React.useEffect(() => {
    if (authentication.auth_type === 'user' && !componentProps?.properties?.preventRedirect) {
      // is logged in so let's redirect him back to homepage or some redirect url
      if (localStorage.getItem('redirect')) {
        const redirect = localStorage.getItem('redirect');
        localStorage.removeItem('redirect');
        navigate(redirect ?? '/');
        return;
      }
      navigate('/');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authentication, componentProps]);

  React.useEffect(() => {
    return () => {
      dispatch(resetRegisterPartialAccountData());
      dispatch(clear2FADetails());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [username, setUsername] = React.useState(
    existingAccount && existingAccountData.phone ? existingAccountData.phone : '',
  );
  const [password, setPassword] = React.useState('');
  const [SMSCode, setSMSCode] = React.useState('');
  const [OTP2FA, setOTP2FA] = React.useState('');
  const [showPassword, setShowPassword] = React.useState(false);
  const deferredUsername = React.useDeferredValue(username);
  const deferredPassword = React.useDeferredValue(password);
  const deferredSMSCode = React.useDeferredValue(SMSCode);

  const [smsCodeInRequest, setSmsCodeInRequest] = React.useState(false);
  const [hasMadeOneRequest, setHasMadeOneRequest] = React.useState(false);

  React.useEffect(() => {
    let timer: any = null;
    if (code2FADetails && code2FADetails.expires_in) {
      setCode2FAExpiresIn(code2FADetails.expires_in);
      timer = setInterval(() => {
        setCode2FAExpiresIn((prev) => {
          if (prev !== null) {
            if (prev <= 1) {
              clearInterval(timer);
              return null;
            }
            return prev - 1;
          }
          return null;
        });
      }, 1000);
    } else {
      setCode2FAExpiresIn(null);
    }
    return () => {
      if (timer) {
        clearInterval(timer);
      }
    };
  }, [code2FADetails]);

  React.useEffect(() => {
    if (deferredUsername && username === deferredUsername) {
      dispatch(loginUsername(deferredUsername));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [username, deferredUsername]);

  React.useEffect(() => {
    if (deferredPassword && password === deferredPassword) {
      dispatch(loginPassword(deferredPassword));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [password, deferredPassword]);

  React.useEffect(() => {
    if (deferredSMSCode && SMSCode === deferredSMSCode) {
      dispatch(loginPassword(deferredSMSCode));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [SMSCode, deferredSMSCode]);

  const showPasswordHandler = React.useCallback(() => {
    setShowPassword(!showPassword);
  }, [showPassword]);

  const isSmsCodeInRequest = React.useCallback(() => {
    if (!hasMadeOneRequest) {
      setHasMadeOneRequest(true);
    }
    setSmsCodeInRequest(true);

    setTimeout(() => {
      setSmsCodeInRequest(false);
    }, 2000);
  }, [hasMadeOneRequest]);

  const resendTempSMSCodeHandler = React.useCallback(() => {
    if (login.etaSmsRequest <= 1) {
      dispatch(requestSmsCode());
      isSmsCodeInRequest();
      setSMSCode('');
    }
  }, [dispatch, isSmsCodeInRequest, login.etaSmsRequest]);

  // const [isValidPhone, setIsValidPhone] = React.useState(false);
  // const phoneValidator = (phone: string) => {
  // PhoneNumberValidator.validate(phone, currentCountry).then((res) => {
  //   setIsValidPhone(res);
  // });
  // };

  // React.useEffect(() => {
  //   if (login.method === IS_PHONE && username) {
  //     phoneValidator(username);
  //   }
  // }, [username, login.method]);

  // dispatch clear login error if username or password changes
  React.useEffect(() => {
    dispatch(loginError(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [username, password, SMSCode]);

  React.useEffect(() => {
    if (login.errorUsername || login.errorPassword || login.loginError) {
      setCurrentStep('login');
    }
  },[login.errorUsername,login.errorPassword, login.loginError]);

  const getUserNameErrorMsg = React.useCallback(() => {
    const phoneRegex = /^\d+$/;
    let err = '';
    if (
      window.config.authenticationSettings.enableEmail === '1' &&
      username &&
      username.length >= 4 &&
      !phoneRegex.test(username)
    ) {
      if (login.errorUsername || !username.includes('@')) {
        if (login.errorUsername) {
          err = t('Invalid email format, verify your email and try again');
        }
        if (!username.includes('@')) {
          err = t("Invalid email format. Missing '@' character");
        }
      }
    }

    if (
      window.config.authenticationSettings.enablePhone === '1' &&
      username &&
      username.length > 4 &&
      phoneRegex.test(username)
    ) {
      if (login.errorUsername) {
        err = t('Please enter a valid phone number');
      }
    }

    return err;
  }, [login.errorUsername, t, username]);

  const goToForgotPass = React.useCallback(() => {
    events.send(GLOBAL_FLUTTER_CHANNELS.POKER_APP_EVENT, {
      event: GLOBAL_EVENTS.FORM_FYP,
    });
    navigate('/forgot-password');
  }, [events, navigate]);

  const isBackDisabled = React.useMemo(() => {
    return currentStep === 'login';
  }, [currentStep]);

  const goBackHandler = React.useCallback(() => {
    if (!isBackDisabled) {
      if (currentStep === '2fa') {
        setCurrentStep('select-2fa-method');
      } else {
        setCurrentStep('login');
      }
    }
  }, [isBackDisabled]);

  const onChangeOTP2FAHandler = React.useCallback((e: React.FormEvent<HTMLInputElement>) => {
    const value = e.target ? (e.target as HTMLInputElement).value : '';
    setOTP2FA(value);
    // dispatch(loginPassword(value));
  }, []);




  const context2FA = React.useMemo(() => ({
      selected2FAMethod,
      methods2FA,
      currentStep: currentStep,
      OTP2FA,
      onChangeOTP2FAHandler,
      confirm2FASelectedMethod,
      isBackDisabled,
      goBackHandler,
      expiresIn: code2FAExpiresIn,
  }), [selected2FAMethod, methods2FA, currentStep, OTP2FA, onChangeOTP2FAHandler, confirm2FASelectedMethod, isBackDisabled, goBackHandler, code2FAExpiresIn]);

  const contextValue = {
    context2FA,
    title: login.needsResetPassword ? 'LOGIN_TITLE_NEEDS_RESET_PASSWORD' : props?.properties?.title,
    subTitle: login.needsResetPassword
      ? 'LOGIN_SUBTITLE_NEEDS_RESET_PASSWORD'
      : existingAccount
        ? 'LOGIN_SUBTITLE_REGISTER_EXISTING_ACCOUNT'
        : props?.properties?.subTitle,
    usernameError: false,
    usernameErrorMsg: getUserNameErrorMsg(),
    passwordError: false,

    smsCodeError: false,
    loginError: login.loginError,
    canLogin: true,
    username: username,
    password: password,
    SMSCode: SMSCode,
    resendTempSMSCode: resendTempSMSCodeHandler,
    inRequest: login.inRequest,
    smsCodeInRequest: smsCodeInRequest,
    hasMadeOneRequest: hasMadeOneRequest,
    // isValidPhone: isValidPhone,
    etaSmsRequest: login.etaSmsRequest ? login.etaSmsRequest : '',
    showPassword: showPassword,
    loginMethod: login.method,
    needsResetPassword: login.needsResetPassword,
    goToForgotPass,
    existingAccount,
    devMode: import.meta.env.MODE === 'development',
    showPasswordHandler: showPasswordHandler,
    onChangeUsername: (e: React.FormEvent<HTMLInputElement>) => {
      const value = e.target ? (e.target as HTMLInputElement).value : '';
      // force login method check in saga
      dispatch(loginMethod(IS_UNDEFINED));
      setUsername(value);
      if (!value) {
        setPassword('');
        setSMSCode('');
      }
    },
    onChangePassword: (e: React.FormEvent<HTMLInputElement>) => {
      const value = e.target ? (e.target as HTMLInputElement).value : '';
      setPassword(value);
      dispatch(loginPassword(value));
    },
    onChangeSMSHandler: (e: React.FormEvent<HTMLInputElement>) => {
      const value = e.target ? (e.target as HTMLInputElement).value : '';
      setSMSCode(value);
    },
    onDoLogin: (e: any) => {
      // console.log('running doLogin:', { e, username, password, login, SMSCode, partialAccount: existingAccountData });
      dispatch(resetRegisterPartialAccountData());
      if (username && OTP2FA && login.method === IS_2FA && OTP2FA.length >= 4 && currentStep === '2fa') {
        dispatch(loginMethod(IS_2FA));
        dispatch(loginPassword({ OTP2FA: OTP2FA, password:password }));
        dispatch(loginStart(undefined));
        return;
      }
      if (
        username &&
        (password || SMSCode) &&
        login.method === IS_USERNAME &&
        (login.allowSMS || login.allowLoginButton)
      ) {
        if (e.key === 'Enter' || e.currentTarget.dataset.id === 'login') {
          dispatch(loginStart(undefined));
        }
      }

      if (username && password && login.method === IS_EMAIL && login.allowLoginButton) {
        if (e.key === 'Enter' || e.currentTarget.dataset.id === 'login') {
          dispatch(loginStart(undefined));
        }
      }


      if (username && login.method === IS_PHONE && (login.allowSMS || login.allowLoginButton)) {
        if (e.key === 'Enter' || e.currentTarget.dataset.id === 'login') {
          if (
            window.config.authenticationSettings?.enablePhone === '1' &&
            window.config.authenticationSettings?.enableEmail !== '1'
          ) {
            // africa login: phone and password
            dispatch(loginStart(undefined));
          } else {
            if (SMSCode.length >= 4) {
              dispatch(loginStart(undefined));
            } else {
              dispatch(requestSmsCode());
              isSmsCodeInRequest();
            }
          }
        }
        return false;
      }
    },
  };

  if (login.errorUsername) {
    contextValue.usernameError = true;
  }
  if (login.errorPassword) {
    contextValue.passwordError = true;
  }

  // Africa login: phone and password
  if (
    window.config.authenticationSettings?.enablePhone === '1' &&
    window.config.authenticationSettings?.enableEmail !== '1' &&
    window.config.authenticationSettings?.enablePassword === '1'
  ) {
    // false if we have phone number error
    login.password && !login.errorUsername ? (contextValue.canLogin = true) : (contextValue.canLogin = false);
  } else {
    if (!login.allowLoginButton || (login.method === IS_PHONE && SMSCode.length < 4)) {
      contextValue.canLogin = false;
    }
  }

  if (SMSCode.length > 0 && SMSCode.length < 4) {
    contextValue.smsCodeError = true;
  }

  // username login (email or phone)
  // no restrictions
  if (
    window.config.authenticationSettings?.enableUsername === '1' &&
    window.config.authenticationSettings?.enableEmail !== '1' &&
    window.config.authenticationSettings?.enablePhone !== '1'
  ) {
    contextValue.canLogin = true;
    contextValue.usernameError = false;
  }

  // console.log('LOGIN contextValue', {
  //   login,
  //   contextValue,
  //   SMSCode,
  //   smsCodeInRequest,
  //   // isValidPhone,
  //   hasMadeOneRequest,
  // });

  return (
    <ModuleElementDiv className={props.className ?? ''} $styleText={props.styleText}>
      <DataElementContext.Provider value={contextValue}>{children}</DataElementContext.Provider>
    </ModuleElementDiv>
  );
};

export default Login;
